import { useEffect, useRef, useState } from 'react';
import { toast } from '../stores/toaster';

/**
 * Micro de dictée vocale (Web Speech API).
 *
 * - fr-FR par défaut (override via `lang`).
 * - `interimResults: true` → on append le segment en cours dès qu'il est
 *   stable, puis on remplace par la version finale à la fin de l'énoncé.
 * - Détection capability au mount : si l'API n'est pas dispo (Firefox,
 *   vieux navigateurs), le composant rend ``null`` (le caller affiche
 *   son input comme avant).
 * - Click pendant l'écoute = stop manuel.
 *
 * Compatibilité : Chrome/Edge desktop, Chrome Android, Safari iOS 14.5+.
 */

type Props = {
  /** Appelé quand un nouveau segment de transcript est prêt (final). */
  onTranscript: (text: string) => void;
  /** ``'sm'`` (32px) pour input compact, ``'md'`` (44px) pour tablette. */
  size?: 'sm' | 'md';
  /** Code langue BCP-47. Défaut : ``fr-FR``. */
  lang?: string;
  /** Désactive le bouton (ex. pendant un envoi en cours). */
  disabled?: boolean;
  /** Tooltip personnalisé. */
  title?: string;
};

type Phase = 'idle' | 'listening' | 'unsupported';

// Globale Web Speech API (préfixée webkit côté Safari + Chromium).
type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance;
type SpeechRecognitionInstance = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((ev: SpeechRecognitionEvent) => void) | null;
  onerror: ((ev: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
};
type SpeechRecognitionEvent = {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultList>;
};
type SpeechRecognitionResultList = {
  isFinal: boolean;
  0: { transcript: string };
};
type SpeechRecognitionErrorEvent = { error: string; message?: string };

function getRecognitionCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export default function DictationButton({
  onTranscript,
  size = 'md',
  lang = 'fr-FR',
  disabled,
  title,
}: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  useEffect(() => {
    if (getRecognitionCtor() === null) {
      setPhase('unsupported');
    }
    return () => {
      // Stop net si le composant unmount pendant l'écoute.
      try {
        recognitionRef.current?.abort();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
    };
  }, []);

  if (phase === 'unsupported') {
    return null;
  }

  const start = () => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      setPhase('unsupported');
      return;
    }
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = false;
    rec.interimResults = true;
    rec.onresult = (ev) => {
      // On accumule uniquement les résultats finalisés pour ne pas
      // injecter du texte qui change. Le segment "interim" est ignoré
      // côté caller — l'utilisateur voit l'input se remplir d'un coup
      // à la fin de chaque phrase.
      let final = '';
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        const r = ev.results[i] as unknown as SpeechRecognitionResultList;
        if (r && r.isFinal) {
          final += r[0].transcript;
        }
      }
      if (final.trim()) {
        onTranscript(final);
      }
    };
    rec.onerror = (ev) => {
      // Erreurs courantes :
      //  - 'no-speech' : silence — on stoppe sans alerter
      //  - 'aborted' : stop manuel
      //  - 'not-allowed' : permission refusée par le navigateur
      //  - 'audio-capture' : pas de micro
      //  - 'network' : OS ne joint pas le service de reconnaissance
      if (ev.error === 'no-speech' || ev.error === 'aborted') return;
      if (ev.error === 'not-allowed') {
        toast.error('Micro refusé par le navigateur — vérifie les permissions du site');
      } else if (ev.error === 'audio-capture') {
        toast.error('Aucun micro détecté');
      } else {
        toast.error(`Reconnaissance vocale : ${ev.error}`);
      }
    };
    rec.onend = () => {
      setPhase('idle');
      recognitionRef.current = null;
    };
    try {
      rec.start();
      recognitionRef.current = rec;
      setPhase('listening');
    } catch (err) {
      // Souvent : "already started". On tente un stop puis re-start.
      toast.error(`Impossible de démarrer le micro : ${(err as Error).message}`);
    }
  };

  const stop = () => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
  };

  const onClick = () => {
    if (phase === 'listening') stop();
    else start();
  };

  const sizeClasses = size === 'sm' ? 'h-8 w-8 text-[14px]' : 'h-11 w-11 text-[18px]';
  const stateClasses =
    phase === 'listening'
      ? 'border-cat-def/60 bg-cat-def-pastel text-cat-def animate-pulse'
      : 'border-rule bg-paper-elevated text-text-secondary hover:border-accent/40 hover:text-accent-text';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title ?? (phase === 'listening' ? "J'écoute… (clic pour arrêter)" : 'Dicter (fr-FR)')}
      aria-label={phase === 'listening' ? 'Arrêter la dictée' : 'Démarrer la dictée vocale'}
      aria-pressed={phase === 'listening'}
      className={`flex shrink-0 items-center justify-center rounded-full border transition disabled:opacity-50 ${sizeClasses} ${stateClasses}`}
    >
      {phase === 'listening' ? '⏹' : '🎤'}
    </button>
  );
}
